// @file Persisted post attachment types and picker-to-setting maps

import { ContentPickerSource } from '../enums'

// Values are persisted as board attachment_settings keys and must never be renamed.
const PostAttachmentType = {
  File: 'file',
  Link: 'link',
  WebSearch: 'web_search',
  PhotoBooth: 'photo_booth',
  VideoRecorder: 'video_recorder',
  AudioRecorder: 'audio_recorder',
  Drawing: 'drawing',
  Padlet: 'padlet',
  LocationSearch: 'location_search',
  ScreenRecorder: 'screen_recorder',
  AiImageSearch: 'ai_image_search',
  ImageSearch: 'image_search',
  ThreeDObjectSearch: 'three_d_object_search',
  VideoSearch: 'video_search',
  AudioSearch: 'audio_search',
  GifSearch: 'gif_search',
  Poll: 'poll',
  GoogleDrive: 'google_drive',
  MicrosoftOneDrivePicker: 'microsoft_one_drive_picker',
  AiTalk: 'ai_talk',
  MediaLibrary: 'media_library',
  DocumentScanner: 'document_scanner',
  PhotoAlbum: 'photo_album',
  Stickers: 'stickers',
  DeviceUploads: 'device_uploads',
  Arcade: 'arcade',
} as const
type PostAttachmentType = (typeof PostAttachmentType)[keyof typeof PostAttachmentType]
const POST_ATTACHMENT_TYPES: readonly PostAttachmentType[] = Object.values(PostAttachmentType)

const DEVICE_UPLOADABLE_ATTACHMENT_TYPES: readonly PostAttachmentType[] = [
  PostAttachmentType.File,
  PostAttachmentType.PhotoBooth,
  PostAttachmentType.VideoRecorder,
  PostAttachmentType.AudioRecorder,
  PostAttachmentType.Drawing,
  PostAttachmentType.PhotoAlbum,
]

// All (`true`) / None (`false`) win before the DeviceUploads flag, so All still
// allows DeviceUploads when the flag is off. The DeviceUploads answer is derived —
// never write it back to storage (see rebuildVisibleAttachmentSettings); boards
// saved before the flip may carry a stale stored `false`.
function isAttachmentTypeAllowed(
  settings: boolean | Partial<Record<PostAttachmentType, boolean>>,
  type: PostAttachmentType,
  { deviceUploadsEnabled }: { deviceUploadsEnabled: boolean },
): boolean {
  if (typeof settings === 'boolean') return settings
  if (type === PostAttachmentType.DeviceUploads) {
    if (!deviceUploadsEnabled) return false
    const hasUploadableType = DEVICE_UPLOADABLE_ATTACHMENT_TYPES.some(
      (uploadableType) => settings[uploadableType] === true,
    )
    if (!hasUploadableType) return false
    return settings[PostAttachmentType.DeviceUploads] === true
  }
  return settings[type] === true
}

const ATTACHMENT_TYPE_BY_PICKER_SOURCE: Record<ContentPickerSource, PostAttachmentType> = {
  [ContentPickerSource.FileBrowser]: PostAttachmentType.File,
  [ContentPickerSource.LinkInput]: PostAttachmentType.Link,
  [ContentPickerSource.WebBrowser]: PostAttachmentType.WebSearch,
  [ContentPickerSource.PhotoBooth]: PostAttachmentType.PhotoBooth,
  [ContentPickerSource.VideoRecorder]: PostAttachmentType.VideoRecorder,
  [ContentPickerSource.AudioRecorder]: PostAttachmentType.AudioRecorder,
  [ContentPickerSource.DrawingCanvas]: PostAttachmentType.Drawing,
  [ContentPickerSource.PadletBrowser]: PostAttachmentType.Padlet,
  [ContentPickerSource.MapBrowser]: PostAttachmentType.LocationSearch,
  [ContentPickerSource.ScreenshareRecorder]: PostAttachmentType.ScreenRecorder,
  [ContentPickerSource.AiImageSearch]: PostAttachmentType.AiImageSearch,
  [ContentPickerSource.ImageSearch]: PostAttachmentType.ImageSearch,
  [ContentPickerSource.VideoSearch]: PostAttachmentType.VideoSearch,
  [ContentPickerSource.AudioSearch]: PostAttachmentType.AudioSearch,
  [ContentPickerSource.GifSearch]: PostAttachmentType.GifSearch,
  [ContentPickerSource.Poll]: PostAttachmentType.Poll,
  [ContentPickerSource.GoogleDrive]: PostAttachmentType.GoogleDrive,
  [ContentPickerSource.MicrosoftOneDrive]: PostAttachmentType.MicrosoftOneDrivePicker,
  [ContentPickerSource.AiTalk]: PostAttachmentType.AiTalk,
  [ContentPickerSource.MediaLibrary]: PostAttachmentType.MediaLibrary,
  [ContentPickerSource.PhotoAlbum]: PostAttachmentType.PhotoAlbum,
  [ContentPickerSource.DeviceUploads]: PostAttachmentType.DeviceUploads,
}

export {
  ATTACHMENT_TYPE_BY_PICKER_SOURCE,
  DEVICE_UPLOADABLE_ATTACHMENT_TYPES,
  isAttachmentTypeAllowed,
  POST_ATTACHMENT_TYPES,
  PostAttachmentType,
}
